import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { postDataAPI, clearStaleSelectedArtistAndCaches, getActiveArtistIds, toggleActiveArtistId, setArtistCache, setActiveArtistIds, TEAM_CONTEXT_CACHE_PREFIXES } from '../lib/api'
import { getDropdownPositionLeft } from '../lib/dropdownPosition'

type ManagedArtist = {
  _id: string
  id?: string
  name: string
  email: string
  role: string
  profilePicture?: string
  followers?: number
  accountType?: 'artist' | 'promoter'
}

function getInitials(name: string) {
  if (!name) return '??'
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getRoleDisplay(role: string) {
  return role === 'manager' ? 'Member' : role === 'support' ? 'Support' : role === 'promoter' ? 'Promoter' : 'Team Member'
}

type ArtistSwitcherProps = {
  /** Icon-only trigger when the sidebar is collapsed — still opens the same team/artist picker. */
  compact?: boolean
}

export default function ArtistSwitcher({ compact = false }: ArtistSwitcherProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [managedArtists, setManagedArtists] = useState<ManagedArtist[]>([])
  /** False until managed-artists request finishes — avoids sidebar layout jump (null → full control). */
  const [managedArtistsLoaded, setManagedArtistsLoaded] = useState(false)
  const [activeArtistIds, setActiveArtistIdsState] = useState<string[]>(() => getActiveArtistIds())
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const [promoterArtistAccounts, setPromoterArtistAccounts] = useState<ManagedArtist[]>([])
  const [loadingPromoterArtists, setLoadingPromoterArtists] = useState(false)

  const primaryArtistId = activeArtistIds[0] ?? null

  const [user] = useState<any>(() => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  const token = (() => {
    try {
      const t = localStorage.getItem('dizeeToken')
      if (!t) return null
      const parsed = JSON.parse(t)
      return typeof parsed === 'string' ? parsed : parsed
    } catch {
      return localStorage.getItem('dizeeToken')
    }
  })()

  useEffect(() => {
    const sync = () => setActiveArtistIdsState(getActiveArtistIds())
    sync()
    window.addEventListener('dizee:activeArtistsChanged', sync)
    return () => window.removeEventListener('dizee:activeArtistsChanged', sync)
  }, [])

  useEffect(() => {
    const fetchManaged = async () => {
      if (!user?.email || !token) {
        setManagedArtistsLoaded(true)
        return
      }
      try {
        const res = await postDataAPI('team/managed-artists', { teamMemberEmail: user.email }, token)
        const data = res?.data?.data ?? res?.data ?? res
        const list = Array.isArray(data) ? data : []
        setManagedArtists(list)
        setArtistCache(list)
        const managedIdSet = new Set(list.map((a: ManagedArtist) => a._id || a.id))
        let activeIds = getActiveArtistIds()

        if (activeIds.some((id) => !managedIdSet.has(id))) {
          clearStaleSelectedArtistAndCaches()
          activeIds = []
          setActiveArtistIdsState([])
        }

        // Default all artists ON for first sign-in or after stale-clear
        if (activeIds.length === 0 && list.length > 0) {
          let restored = false
          const keysToTry = [user?.email, user?._id, user?.id].filter(Boolean)
          for (const uid of keysToTry) {
            if (restored) break
            const saved = localStorage.getItem(`dizee:activeArtistIds:${uid}`)
            if (saved) {
              try {
                const parsed = JSON.parse(saved)
                const valid = Array.isArray(parsed) ? parsed.filter((id: string) => managedIdSet.has(id)) : []
                if (valid.length > 0) {
                  setActiveArtistIds(valid)
                  setActiveArtistIdsState(valid)
                  restored = true
                }
              } catch { /* ignore */ }
            }
          }
          if (!restored) {
            const allIds = list.map((a: ManagedArtist) => a._id || a.id).filter((id): id is string => typeof id === 'string')
            setActiveArtistIds(allIds)
            setActiveArtistIdsState(allIds)
          }
          window.dispatchEvent(new CustomEvent('dizee:activeArtistsChanged'))
        }
      } catch (e) {
        console.error('Error fetching managed artists:', e)
      } finally {
        setManagedArtistsLoaded(true)
      }
    }
    fetchManaged()
  }, [user?.email, token])

  useEffect(() => {
    const promoterId = primaryArtistId && managedArtists.find((a) => (a._id || a.id) === primaryArtistId)?.accountType === 'promoter' ? primaryArtistId : null
    if (!promoterId || !token) {
      setPromoterArtistAccounts([])
      return
    }
    setLoadingPromoterArtists(true)
    postDataAPI('team/promoter-artist-accounts', { promoterId }, token)
      .then((res) => {
        const raw = res?.data?.data ?? res?.data ?? res
        const list = Array.isArray(raw) ? raw : []
        setPromoterArtistAccounts(list)
      })
      .catch(() => setPromoterArtistAccounts([]))
      .finally(() => setLoadingPromoterArtists(false))
  }, [primaryArtistId, managedArtists, token])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      // Calculate dropdown position when opening (flip above if near bottom)
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        setDropdownPosition(getDropdownPositionLeft(rect, 320))
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  /** Clear all page-level caches so stale data from previous artist doesn't flash. */
  const clearAllPageCaches = () => {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && TEAM_CONTEXT_CACHE_PREFIXES.some((p) => key.startsWith(p))) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k))
    sessionStorage.clear()
  }

  const handleToggleArtist = (artist: ManagedArtist) => {
    const id = artist._id || artist.id
    if (!id) return
    clearAllPageCaches()
    const next = toggleActiveArtistId(id)
    setActiveArtistIdsState(next)
    window.dispatchEvent(new CustomEvent('dizee:activeArtistsChanged'))
    setIsOpen(false)
  }

  const isTeamMember = user?.professionRole && ['manager', 'support', 'promoter'].includes(user.professionRole)
  const isActive = (artist: ManagedArtist) => activeArtistIds.includes(artist._id || artist.id || '')

  if (!isTeamMember) {
    return null
  }

  if (!managedArtistsLoaded) {
    return compact ? (
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800 animate-pulse"
        aria-busy="true"
        aria-label="Loading team switcher"
      />
    ) : (
      <div
        className="h-10 w-full max-w-[min(100%,16rem)] rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800 animate-pulse"
        aria-busy="true"
        aria-label="Loading team switcher"
      />
    )
  }

  if (managedArtists.length === 0) {
    return null
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        title="Your teams — switch active artists"
        aria-label="Your teams — switch active artists"
        className={
          compact
            ? `sidebar-tooltip-wrap flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 ${isOpen ? 'ring-2 ring-indigo-500/40' : ''}`
            : 'flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm'
        }
      >
        {compact ? (
          <span className="flex h-8 w-8 items-center justify-center text-gray-500 dark:text-gray-400 [&_svg]:block">
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span className="sidebar-tooltip">Your teams</span>
          </span>
        ) : (
          <>
            <div className="text-sm font-medium text-gray-900 dark:text-white">Team</div>
            <svg
              className={`w-3 h-3 text-gray-500 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
              style={{ transition: 'transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {isOpen && createPortal(
        <>
          <div className="fixed inset-0 z-[70]" onClick={() => setIsOpen(false)} />
          <div 
            ref={dropdownRef}
            className="fixed w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[71]"
            style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
          >
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Your Teams
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
              {managedArtists.map((artist) => (
                <button
                  key={artist._id}
                  type="button"
                  onClick={() => handleToggleArtist(artist)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                    style={{ background: 'linear-gradient(45deg, #6366f1, #8b5cf6)' }}
                  >
                    {artist.profilePicture ? (
                      <img
                        src={artist.profilePicture}
                        alt=""
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      getInitials(artist.name)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {artist.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      As {getRoleDisplay(artist.role)}
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${isActive(artist) ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                    {isActive(artist) ? 'On' : 'Off'}
                  </span>
                </button>
              ))}
            </div>
            {primaryArtistId && managedArtists.find((a) => (a._id || a.id) === primaryArtistId)?.accountType === 'promoter' && promoterArtistAccounts.length > 0 && (
              <div className="border-t border-gray-100 dark:border-gray-700">
                <div className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Artist accounts
                </div>
                {loadingPromoterArtists ? (
                  <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">Loading…</div>
                ) : (
                  promoterArtistAccounts.map((artist) => (
                    <button
                      key={artist._id}
                      type="button"
                      onClick={() => handleToggleArtist(artist)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                        style={{ background: 'linear-gradient(45deg, #10b981, #059669)' }}
                      >
                        {artist.profilePicture ? (
                          <img
                            src={artist.profilePicture}
                            alt=""
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(artist.name)
                        )}
                      </div>
                      <div className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-white truncate">
                        {artist.name}
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded flex-shrink-0 ${isActive(artist) ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                        {isActive(artist) ? 'On' : 'Off'}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
            <div className="border-t border-gray-200 dark:border-gray-700">
              <Link
                to="/team"
                onClick={() => setIsOpen(false)}
                className="block w-full px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-center"
              >
                Go to Team
              </Link>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  )
}
